import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../../components/tailwind-component/ui/dialog';
import { ZapTourGuide } from '../../ZapTourModal';
import '../../zap-tour.scss';
import { StepHelpContent } from './stepHelpContent';

/* The ⓘ next to "Step X of 4" and what it opens.

   Both the help and the full guide are nested Radix dialogs. The wizard itself
   is a Radix dialog, and Radix only lets its own nested layers take clicks,
   scroll and focus while it is open — an MUI modal over it would be inert. */

const ACCENT = '#7C3AED';
// The screenshot buttons are just clickable images; undo the app's global button box.
const BARE_BUTTON: React.CSSProperties = { background: 'none', border: 0, padding: 0, cursor: 'pointer' };

export function StepHelpButton({ onClick, pulse }: { onClick: () => void; pulse: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Help for this step"
      aria-haspopup="dialog"
      className={`inline-flex items-center justify-center rounded-full text-white shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-violet-400${pulse ? ' animate-pulse' : ''}`}
      style={{
        width: 22,
        height: 22,
        padding: 0,
        border: 0,
        // Inline, not rounded-full: the app's global button styles win over the utility.
        borderRadius: '50%',
        backgroundColor: ACCENT,
        font: 'italic 700 13px/1 Georgia, "Times New Roman", serif',
        cursor: 'pointer',
      }}
    >
      i
    </button>
  );
}

interface StepHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kicker?: string;
  content: StepHelpContent;
  onOpenGuide: () => void;
}

export function StepHelpDialog({ open, onOpenChange, kicker, content, onOpenGuide }: StepHelpDialogProps) {
  // Clicking the thumbnail swaps the panel to the full-size screenshot.
  const [zoomed, setZoomed] = useState(false);
  // Start on "Got it" — Radix otherwise focuses the first control, the screenshot.
  const gotItRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setZoomed(false);
        onOpenChange(o);
      }}
    >
      {/* transform: 'inherit' — same fix the wizard's own DialogContent needs;
          without it the app's global CSS knocks the dialog off-centre. */}
      <DialogContent
        className="sm:max-w-md max-h-[90vh] overflow-y-auto"
        style={{ transform: 'inherit' }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          gotItRef.current?.focus();
        }}
      >
        <DialogHeader className="text-left">
          {kicker && (
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: ACCENT }}>
              {kicker}
            </span>
          )}
          <DialogTitle>{content.title}</DialogTitle>
          <DialogDescription className="sr-only">Help for this step of creating a Zap.</DialogDescription>
        </DialogHeader>

        {zoomed && content.image ? (
          <button type="button" onClick={() => setZoomed(false)} className="block mx-auto" style={BARE_BUTTON} aria-label="Back to help">
            <img
              src={content.image}
              alt={`Example: ${content.title}`}
              className="rounded-2xl border-4 border-gray-900"
              style={{ maxHeight: '70vh', width: 'auto' }}
            />
            <span className="block text-xs text-gray-500 mt-2 text-center">Click to go back</span>
          </button>
        ) : (
          <div className="flex gap-4 items-start">
            {content.image && (
              <button type="button" onClick={() => setZoomed(true)} className="shrink-0" style={BARE_BUTTON} aria-label="Enlarge example screenshot">
                <img
                  src={content.image}
                  alt={`Example: ${content.title}`}
                  className="rounded-xl border-[3px] border-gray-900 cursor-zoom-in"
                  style={{ width: 112 }}
                />
                <span className="block text-[11px] text-gray-400 mt-1 text-center">Click to enlarge</span>
              </button>
            )}
            <ul className="flex flex-col gap-2.5 text-sm text-gray-700 leading-relaxed">
              {content.points.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-2 shrink-0 rounded-full" style={{ width: 6, height: 6, backgroundColor: ACCENT }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!zoomed && content.tip && (
          <div className="rounded-lg px-3 py-2 text-sm font-semibold" style={{ backgroundColor: '#F5F3FF', color: '#5B21B6' }}>
            💡 {content.tip}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onOpenGuide}
            className="flex-1 rounded-lg py-2.5 text-sm font-bold"
            style={{ border: `1.5px solid ${ACCENT}`, color: ACCENT, cursor: 'pointer' }}
          >
            Full guide
          </button>
          <button
            ref={gotItRef}
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex-1 rounded-lg py-2.5 text-sm font-bold text-white bg-black"
            style={{ cursor: 'pointer' }}
          >
            Got it
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StepGuideDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Same frame as the guide's own modal (ZapTourModal): 95vw, ≤1100px, 90vh.
          The guide brings its own close button, so the dialog's X is hidden. */}
      <DialogContent
        className="p-0 gap-0 overflow-hidden sm:max-w-[1100px] [&>button:last-child]:hidden"
        style={{ width: '95vw', maxWidth: 1100, height: '90vh', display: 'block', transform: 'inherit' }}
      >
        <DialogTitle className="sr-only">How to use Zap</DialogTitle>
        <DialogDescription className="sr-only">Step-by-step guide to creating a Zap.</DialogDescription>
        <ZapTourGuide onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
